import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'
import { BlockUI } from 'primereact/blockui'
import useMountedRef from '@aytm/lib/hooks/useMountedRef'

import { IActionComponentAPI } from '../actions/types'
import { ISessionAPI, ISessionProps } from './types'
import { IViewport, ViewportOrientation } from '../modules/viewport/types'

import { layoutIsCompact } from '../lib/layout'
import getActionComponent from '../actions'
import ToolbarModule from '../modules/toolbar/Module'
import PreviewModule from '../modules/preview/Module'
import InteractionsModule from '../modules/interactions/Module'
import { DEFAULT_MODULES_TO_LOAD } from '../modules'

import LoaderAction from '../actions/loader/Action'
import LoaderView from '../actions/loader/View'
import LocaleContext from '../modules/locale/Locale.Context'
import useLoadedModules from '../modules/useLoadedModules'
import useViewport from '../modules/viewport/useViewport'
import NavigationPanel from './NavigationPanel/NavigationPanel'
import ProgressBar from './ProgressBar/ProgressBar'
import SideText from './SideText/SideText'
import InIFrame from '@aytm/lib/common/components/InIFrame/InIFrame'

import SessionAPIContext from './Session/SessionAPI.Context'
import useCurrentActionState from './Session/useCurrentActionState'
import useCurrentLocalesState from './Session/useCurrentLocalesState'
import useNavigationState from './Session/useNavigationState'
import useNextButtonState from './Session/useNextButtonState'
import usePrevButtonState from './Session/usePrevButtonState'
import useProgressState from './Session/useProgressState'
import useFeaturesState from './Session/useFeaturesState'
import useRefreshView from './Session/useRefreshView'
import useSessionAPIState from './Session/useSessionAPIState'
import useSessionWidgetWidthState from './Session/useSessionWidgetWidthState'
import useRepeatVideos from './Session/useRepeatVideos'
import useScreenNumberState from './Session/useScreenNumberState'
import useCustomMessagesTextState from './Session/useCustomMessagesTextState'
import useCustomStyleState from './Session/useCustomStyleState'
import useSyncPreviewOptions from './Session/useSyncPreviewOptions'
import useSyncTheme from './Session/useSyncTheme'
import useInterfaceLocale from '../modules/locale/useInterfaceLocale'
import useTheme from '../modules/theme/useTheme'

import MobilePortraitSVG from '../modules/preview/images/mobile-portrait.svg'

import style from './style.module.scss'

const Session = React.forwardRef<ISessionAPI, ISessionProps>(
  ({ action, sessionProps, onClosePreview }: ISessionProps, ref) => {
    const refreshView = useRefreshView()
    const mountedRef = useMountedRef()
    const rootRef = useRef<HTMLDivElement>(null)

    const modulesLoaded = useLoadedModules(DEFAULT_MODULES_TO_LOAD)
    const viewport = useViewport()
    const theme = useTheme()
    const viewportRef = useRef<IViewport>()
    viewportRef.current = viewport

    const widgetWidthState = useSessionWidgetWidthState(viewportRef, sessionProps.layout)

    const currentActionState = useCurrentActionState(action)
    const currentAction = currentActionState.currentActionRef.current

    const navigationState = useNavigationState()

    const currentLocalesState = useCurrentLocalesState(currentActionState, mountedRef, modulesLoaded)

    const currentActionComponentRef = useRef<IActionComponentAPI>(null)

    const screenNumberState = useScreenNumberState()

    const nextButtonState = useNextButtonState(
      currentActionState,
      currentActionComponentRef,
      navigationState,
      screenNumberState,
      mountedRef
    )

    const prevButtonState = usePrevButtonState(
      currentActionState,
      currentActionComponentRef,
      navigationState,
      screenNumberState,
      mountedRef
    )

    const progressState = useProgressState(refreshView)
    const currentProgress = progressState.progressRef.current

    const featuresState = useFeaturesState(currentActionState)

    const repeatVideosState = useRepeatVideos()

    const customMessagesTextState = useCustomMessagesTextState(currentActionState)

    const customStyleState = useCustomStyleState()

    const themeState = useSyncTheme(currentActionState, customStyleState, rootRef)

    const apiState = useSessionAPIState(
      sessionProps,
      currentActionState,
      navigationState,
      nextButtonState,
      prevButtonState,
      currentLocalesState,
      widgetWidthState,
      progressState,
      featuresState,
      repeatVideosState,
      screenNumberState,
      customMessagesTextState,
      customStyleState,
      themeState,
      refreshView,
      currentActionComponentRef,
      viewportRef,
      mountedRef
    )
    const sessionAPI = apiState.sessionAPIRef.current
    useImperativeHandle<ISessionAPI, ISessionAPI>(ref, () => sessionAPI, [sessionAPI])

    useSyncPreviewOptions(sessionAPI.surveyMode)

    const { localeOptions } = useInterfaceLocale()
    const [collapsedToolbar, setCollapsedToolbar] = useState(false)

    const showSideTextButton =
      !PreviewModule.isHeadless &&
      (PreviewModule.show
        ? viewport.mobile // we show mobile side text in preview only when mobile mode is selected
        : viewport.width < 1100)

    useEffect(() => {
      // autoscroll on action change
      sessionAPI.scrollIntoView()
      if (sessionProps.fill) {
        const scroller = apiState.sessionElementRef.current?.getElementsByClassName(style.BlockUI)[0]
        if (scroller) scroller.scrollTop = 0
      }
    }, [currentAction, currentActionState.currentActionKey, sessionAPI, sessionProps.fill])

    useEffect(() => {
      if (currentAction?.interactions && !InteractionsModule.enabled) {
        InteractionsModule.configure(currentAction.interactions, currentAction.aytm_domain)
      }
    }, [currentAction])

    const ActionComponent = getActionComponent(currentAction)
    const body = (
      <div
        className={`${style.Session} ${style[`layout-${sessionProps.layout}`] || ''} ${
          sessionProps.fill ? style.fill : ''
        } ${viewport.mobile || theme.style.mobile ? style.mobile : ''} ${
          viewport.emulated ? style.SessionNoMargin : ''
        } ${
          !sessionAPI.style.pb && !viewport.mobile && !theme.style.mobile && !layoutIsCompact(sessionProps.layout)
            ? style.paddingTop
            : ''
        }`}
        style={widgetWidthState.widgetWidthStyleRef.current}
        role="main"
        ref={apiState.sessionElementRef}
      >
        {themeState.customAccentColorCSSRef.current ? (
          <style type="text/css" key="customAccentColorCSS">
            {themeState.customAccentColorCSSRef.current}
          </style>
        ) : null}
        {!viewport.emulated && onClosePreview && ToolbarModule.renderClosePreviewButton({ onClosePreview })}

        {/* {customMessagesTextState.titleTextRef.current && !viewport.mobile && (
          <span
            className={style.surveyTitle}
            dangerouslySetInnerHTML={{ __html: customMessagesTextState.titleTextRef.current }}
            style={{ marginBottom: sessionAPI.style.pb ? 0 : 20 }}
            data-testid="SW-SurveyTitle"
            key="surveyTitle"
          />
        )} */}
        {sessionAPI.style.pb && !sessionAPI.style.hidePb && !viewport.mobile ? (
          <ProgressBar isRtl={localeOptions.rtl} total={currentProgress.total} current={currentProgress.current} />
        ) : null}
        {modulesLoaded && currentLocalesState.localesLoaded ? (
          <>
            {ToolbarModule.renderSurveyToolbar({ sessionProps, sessionAPI, action, collapsedToolbar })}
            <BlockUI
              blocked={navigationState.navigationLoading}
              template={<LoaderView delay={3000} />}
              containerClassName={style.BlockUI}
            >
              {customMessagesTextState.sideTextRef.current && !showSideTextButton ? (
                <SideText
                  action={currentAction}
                  mobileLayout={false}
                  text={customMessagesTextState.sideTextRef.current}
                />
              ) : null}
              <ActionComponent
                action={currentAction}
                ref={currentActionComponentRef}
                key={currentActionState.currentActionKey}
              />
            </BlockUI>
          </>
        ) : (
          <LoaderAction action={currentAction} ref={currentActionComponentRef} />
        )}

        <NavigationPanel
          action={currentAction}
          progressTotal={currentProgress.total}
          progressCurrent={currentProgress.current}
          progressQuestionTime={currentProgress.questionTime}
          onPrevRef={prevButtonState.onActionRef}
          onNextRef={nextButtonState.onActionRef}
          onLoadingRef={navigationState.onNavigationLoadingRef}
          enableRestart={Boolean(currentAction?.enableRestart)}
          showRepeatVideos={repeatVideosState.showRepeatVideos}
          showSideText={showSideTextButton && Boolean(customMessagesTextState.sideTextRef.current)}
          screenNumber={screenNumberState.screenNumberRef.current}
          navigationFrame={sessionAPI.currentQuestionModel?.navigationFrame}
          ref={navigationState.navigationRef}
          key="NavigationPanel"
        />
      </div>
    )

    return (
      <LocaleContext.Provider value={currentLocalesState.currentLocalesRef.current}>
        <SessionAPIContext.Provider value={sessionAPI}>
          <div
            className={`${style.SWidgetRoot} ${sessionProps.fill ? style.SWidgetRootFill : ''} ${sessionAPI.themeClassNames}`}
            ref={rootRef}
          >
            {ToolbarModule.renderPreviewToolbar({ setCollapsedToolbar })}
            {ToolbarModule.renderThemeEditorToolbar({ setCollapsedToolbar, logoURL: sessionAPI.style.logo })}
            {viewport.emulated ? (
              <div
                className={`${style.IFrameWrapper} ${
                  viewport.emulationMobileFrame ? style.IFrameWrapperWithFrame : ''
                }`}
              >
                {viewport.emulationMobileFrame ? (
                  <img
                    src={MobilePortraitSVG}
                    className={`${style.MobileFrame} ${
                      ViewportOrientation.LANDSCAPE === viewport.orientation ? style.MobileFrameLandscape : ''
                    }`}
                  />
                ) : null}
                {onClosePreview &&
                  ToolbarModule.renderClosePreviewButton({
                    onClosePreview,
                    className: style.IFrameCloseButton,
                    style: {
                      transform: `translateX(${viewport.width / 2 + 100}px)`
                    }
                  })}
                <InIFrame
                  width={viewport.width}
                  height={viewport.height}
                  autoHeight={viewport.autoHeight}
                  maxAutoHeight={viewport.maxAutoHeight}
                  onAutoHeightChange={viewport.autoHeight ? viewport.updateAutoHeight : null}
                  style={{ flex: `0 0 ${viewport.width}px` }}
                  className={`${style.IFrame} ${
                    ViewportOrientation.LANDSCAPE === viewport.orientation ? style.IFrameLandscape : ''
                  }`}
                  copyStyles={true}
                  syncStyles={true}
                  noScrollBar={viewport.emulationNoScrollBar}
                  scrollContainer={viewport.emulationScrollContainer}
                  emulateMobileCursor={viewport.emulationMobileCursor}
                >
                  <div className={`${style.SWidgetRoot} ${sessionAPI.themeClassNames}`}>{body}</div>
                </InIFrame>
              </div>
            ) : (
              body
            )}
          </div>
        </SessionAPIContext.Provider>
      </LocaleContext.Provider>
    )
  }
)

Session.displayName = 'Session'
export default Session
